import { apiBuilder, prefixApi } from "@zodios/core";
import { z } from "zod";

export const tagEntityTypeSchema = z.enum([
  "PRODUCT",
  "ADVERTISEMENT",
  "ORGANIZATION",
  "KEYWORD-TARGET",
  "AUDIENCE",
  "PRICING_PLAN",
]);
export type TagEntityType = z.infer<typeof tagEntityTypeSchema>;

const tagScopeSchema = z.enum(["MARKETPLACE", "ORGANIZATION", "AGENCY"]);
export type TagScope = z.infer<typeof tagScopeSchema>;

// Enum-to-key maps. Consumers translate via t(($) => $[TAG_SCOPE_LABELS[scope]]).
// The leaf values are the English labels the user sees, so JSON keys remain
// human-readable per the sentence-key convention.
export const TAG_SCOPE_LABELS = {
  MARKETPLACE: "Marketplace",
  ORGANIZATION: "Organization",
  AGENCY: "Agency",
} as const satisfies Record<TagScope, string>;

const tagVisibilitySchema = z.enum(["USER", "STAFF", "ANYONE"]);
export type TagVisibility = z.infer<typeof tagVisibilitySchema>;

export const TAG_VISIBILITY_LABELS = {
  USER: "Only me",
  STAFF: "Staff only",
  ANYONE: "Anyone",
} as const satisfies Record<TagVisibility, string>;

export const scopeDefinitionSchema = z.object({
  scope: tagScopeSchema,
  visibility: tagVisibilitySchema,
  entity_id: z.string(),
});

const tagSchema = z.object({
  id: z.number(),
  marketplace: z.string().catch(""),
  type: z.string(),
  origin: z.string().catch(""),
  entity_type: tagEntityTypeSchema,
  scope_definition: scopeDefinitionSchema,
  name: z.string(),
  color: z.string(),
  description: z.string().catch(""),
  created_on: z.string().datetime(),
  updated_on: z.string().datetime(),
});

export type Tag = z.infer<typeof tagSchema>;

const tagListResponseSchema = z.object({
  count: z.number(),
  next: z.string().nullable(),
  previous: z.string().nullable(),
  results: z.array(tagSchema),
});

const createTagRequestSchema = z.object({
  name: z.string().min(1),
  entity_type: tagEntityTypeSchema,
  scope_definition: scopeDefinitionSchema.optional(),
  color: z.string().optional(),
  description: z.string().optional().default(""),
  marketplace: z.string().optional(),
  type: z.string(),
  origin: z.string(),
});

export type CreateTagRequest = z.infer<typeof createTagRequestSchema>;

const updateTagRequestSchema = z.object({
  name: z.string().min(1).optional(),
  scope_definition: scopeDefinitionSchema.optional(),
  color: z.string().optional(),
  description: z.string().optional(),
  marketplace: z.string().optional(),
  type: z.string(),
  entity_type: tagEntityTypeSchema,
});

export type UpdateTagRequest = z.infer<typeof updateTagRequestSchema>;

export const tagsApiDefinitions = prefixApi(
  "/api",
  apiBuilder({
    alias: "getTags",
    method: "get",
    path: "/tags/",
    parameters: [
      {
        name: "entity_type",
        type: "Query",
        schema: tagEntityTypeSchema.optional(),
      },
      {
        name: "marketplace",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "type",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "origin",
        type: "Query",
        schema: z.string().optional(),
      },
    ],
    status: 200,
    response: tagListResponseSchema,
  })
    .addEndpoint({
      alias: "createTag",
      method: "post",
      path: "/tags/",
      parameters: [{ name: "body", type: "Body", schema: createTagRequestSchema }],
      status: 201,
      response: tagSchema,
    })
    .addEndpoint({
      alias: "updateTag",
      method: "put",
      path: "/tags/:id/",
      parameters: [
        {
          name: "id",
          type: "Path",
          schema: z.number(),
        },
        { name: "body", type: "Body", schema: updateTagRequestSchema },
      ],
      status: 200,
      response: tagSchema,
    })
    .addEndpoint({
      alias: "deleteTag",
      method: "delete",
      path: "/tags/:id/",
      parameters: [
        {
          name: "id",
          type: "Path",
          schema: z.number(),
        },
      ],
      status: 204,
      response: z.void(),
    })
    .build(),
);
